import React from 'react';

/**
 * Premium Smithy Browns logo system.
 * Composes the chef portrait with a stylised Jamaican flag backdrop and wordmark.
 * Variants:
 *   - full   : large hero usage (vertical lockup)
 *   - header : horizontal lockup for top navigation
 *   - mobile : compact horizontal lockup for small headers
 *   - icon   : square badge for favicons / bottom nav
 */

const CHEF_SRC =
  'https://customer-assets.emergentagent.com/job_96ce88b6-ed9b-42cd-90c9-916ef572699e/artifacts/extpecgw_Logo.jpg';

// SVG Jamaican flag backdrop — cross pattern, used as decorative element behind the chef.
const FlagBackdrop = ({ className = '' }) => (
  <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" className={className} aria-hidden="true">
    <defs>
      <linearGradient id="sb-flag-green" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#009A44" />
        <stop offset="100%" stopColor="#007a35" />
      </linearGradient>
      <linearGradient id="sb-flag-black" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#0a0a0a" />
        <stop offset="100%" stopColor="#1a1a1a" />
      </linearGradient>
    </defs>
    {/* 4 triangles forming the Jamaican flag */}
    <polygon points="0,0 50,50 100,0" fill="url(#sb-flag-black)" />
    <polygon points="0,100 50,50 100,100" fill="url(#sb-flag-black)" />
    <polygon points="0,0 50,50 0,100" fill="url(#sb-flag-green)" />
    <polygon points="100,0 50,50 100,100" fill="url(#sb-flag-green)" />
    {/* yellow X cross */}
    <g stroke="#FFD100" strokeWidth="12" fill="none">
      <line x1="0" y1="0" x2="100" y2="100" />
      <line x1="100" y1="0" x2="0" y2="100" />
    </g>
  </svg>
);

const ChefBadge = ({ size = 88 }) => (
  <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
    {/* Glow */}
    <div className="absolute -inset-1 rounded-full bg-[hsl(var(--sb-gold))]/15 blur-md" />
    {/* Outer ring */}
    <div className="absolute inset-0 rounded-full border-2 border-[hsl(var(--sb-gold))]/70" />
    {/* Flag inside ring */}
    <div className="absolute inset-[3px] rounded-full overflow-hidden bg-black">
      <FlagBackdrop className="absolute inset-0 w-full h-full opacity-95" />
      {/* Chef on top */}
      <img
        src={CHEF_SRC}
        alt="Smithy Browns chef"
        className="absolute inset-0 w-full h-full object-cover scale-[1.4] -translate-y-[6%]"
        style={{ mixBlendMode: 'normal' }}
      />
      <div className="absolute inset-0 ring-1 ring-inset ring-black/30 rounded-full" />
    </div>
  </div>
);

const WordMark = ({ size = 'md', tagline = true, className = '' }) => {
  const sizes = {
    sm: { main: 'text-base', sub: 'text-[9px]' },
    md: { main: 'text-xl', sub: 'text-[10px]' },
    lg: { main: 'text-3xl', sub: 'text-[11px]' },
    xl: { main: 'text-5xl', sub: 'text-sm' },
  };
  const s = sizes[size] || sizes.md;
  return (
    <div className={`leading-none ${className}`}>
      <div className={`font-display ${s.main} text-white tracking-wide`}>
        <span className="sb-text-gradient-gold" style={{ WebkitTextStroke: '0.5px rgba(0,0,0,0.4)' }}>SMITHY</span>
        <span className="text-white mx-1">|</span>
        <span className="sb-text-gradient-gold">BROWNS</span>
      </div>
      {tagline && (
        <div className={`mt-1 ${s.sub} uppercase tracking-[0.3em] text-[hsl(var(--sb-gold))]/85 font-semibold`}>Authentic Jamaican · Halal</div>
      )}
    </div>
  );
};

export const Logo = ({ variant = 'header', className = '', taglineOverride }) => {
  if (variant === 'full') {
    return (
      <div className={`inline-flex flex-col items-center text-center ${className}`}>
        <ChefBadge size={140} />
        <div className="mt-4">
          <WordMark size="xl" tagline={taglineOverride !== false} className="text-center" />
        </div>
      </div>
    );
  }
  if (variant === 'mobile') {
    return (
      <div className={`inline-flex items-center gap-2 ${className}`}>
        <ChefBadge size={36} />
        <WordMark size="sm" tagline={false} />
      </div>
    );
  }
  if (variant === 'icon') {
    return <ChefBadge size={48} />;
  }
  // header default
  return (
    <div className={`inline-flex items-center gap-3 ${className}`}>
      <ChefBadge size={52} />
      <WordMark size="md" tagline={taglineOverride !== false} />
    </div>
  );
};

export const LogoIcon = ({ size = 48, className = '' }) => (
  <div className={className}><ChefBadge size={size} /></div>
);

export default Logo;
